import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationsMCPIDsKeys } from "../../../../api/queryKeys";
import TextButton from "../../../../components/buttons/TextButton";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import TagInput from "../../../../components/tags/TagInput.jsx";
import { Toast } from "../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons, severities } from "../../../stack/environment/icons/iconExport";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import {
  IntegrationsMapping,
  mcpMonitoring,
} from "../../../stack/integrations/IntegrationsMapping";

export default function IntegrationMCPConnection({
  integrationData,
  initialValue,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialValue || "");
  const {
    setMCPIDToIntegration,
    testJamfMCPScriptSearchID,
    testIntuneMCPScriptSearchID,
    testKandjiMCPScriptSearchID,
  } = useAPI();
  const { aiPostureMcpEnabled } = useContext(CustomerFeatureFlagsContext);

  const PLACEHOLDER_BY_INTEGRATION = {
    [IntegrationsMapping.intune.codeName]:
      "e.g., b079d9c8-3a14-427f-9da2-4ff7cea6d5ec",
    [IntegrationsMapping.jamf.codeName]: "e.g., 5",
    [IntegrationsMapping.kandji.codeName]:
      "e.g., b9549a02-9dfe-436f-9c0b-32a112410def",
  };

  const SETUP_GUIDE_URL_BY_INTEGRATION = {
    [IntegrationsMapping.jamf.codeName]:
      "https://breezesec.notion.site/Collect-MCP-Servers-from-macOS-endpoint-1f4ae164ff418098b55bd6a18461ad3a",
    [IntegrationsMapping.intune.codeName]:
      "https://breezesec.notion.site/Collect-MCP-Servers-from-Intune-managed-endpoint-2cbae164ff4180fc9485dad1842a44f4",
    [IntegrationsMapping.kandji.codeName]:
      "https://breezesec.notion.site/Collect-MCP-Servers-from-macOS-endpoint-2d2ae164ff41803291d0eba89b7b7158",
  };

  const MCP_TEST_FUNCTION_BY_INTEGRATION = {
    [IntegrationsMapping.jamf.codeName]: testJamfMCPScriptSearchID,
    [IntegrationsMapping.intune.codeName]: testIntuneMCPScriptSearchID,
    [IntegrationsMapping.kandji.codeName]: testKandjiMCPScriptSearchID,
  };

  const placeholder = PLACEHOLDER_BY_INTEGRATION[integrationData.codeName];
  const isUpcomingIntegration =
    integrationData.mcpMonitoring === mcpMonitoring.UPCOMING;
  const guideUrl = SETUP_GUIDE_URL_BY_INTEGRATION[integrationData.codeName];

  const { integrations } = useIsIntegrated();
  const isIntegrated = integrations.includes(integrationData.codeName);

  const setMCPIDMutation = useMutation({
    mutationFn: async () => {
      if (!MCP_TEST_FUNCTION_BY_INTEGRATION[integrationData.codeName])
        throw new Error("Test MCP function not found");
      await MCP_TEST_FUNCTION_BY_INTEGRATION[integrationData.codeName](value);
      await setMCPIDToIntegration(integrationData.codeName, value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationsMCPIDsKeys.all,
      });
      Toast.success("ID verified. Results will appear after the nightly scan.");
    },
    onError: (error) => {
      Toast.error("Check failed. Please contact support.");
    },
  });

  const tooltipTitle =
    isUpcomingIntegration || !aiPostureMcpEnabled
      ? null
      : !isIntegrated
      ? `Connect ${integrationData.displayName} to enable MCP monitoring`
      : null;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap="10px">
          <IntegrationImage
            name={integrationData.codeName}
            width={18}
            newLogo={true}
            showToolTip={false}
          />
          <Typography
            variant="subtitle2"
            color={!isIntegrated ? colors.text_disabled : colors.text_primary}
          >
            {integrationData.displayName}
          </Typography>
          {(!isIntegrated || isUpcomingIntegration) && (
            <CustomTooltip title={tooltipTitle} placement="top">
              <Box>
                <TagInput tagStyle="outlined" color="inherit" size="S">
                  {isUpcomingIntegration ? "Coming soon" : "Not connected"}
                </TagInput>
              </Box>
            </CustomTooltip>
          )}
        </Box>
        <CustomTooltip title={tooltipTitle} placement="top">
          <Box>
            <TextButton
              onClick={() => {
                window.open(guideUrl, "_blank");
              }}
              color="primary"
              size="small"
              endIcon={icons.ExternalLinkCurrentColorIcon}
              disabled={
                isUpcomingIntegration || !isIntegrated || !aiPostureMcpEnabled
              }
            >
              Open Setup guide
            </TextButton>
          </Box>
        </CustomTooltip>
      </Box>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <TextField
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={
            isUpcomingIntegration || !isIntegrated || !aiPostureMcpEnabled
          }
          label="MCP Monitoring ID"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {Boolean(value) && value === initialValue ? (
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color={colors.button_text_success_enabled_text}
                    >
                      ID Verified
                    </Typography>
                    <SvgToIcon
                      IconLogo={severities.SeveritySuccessIcon}
                      width="18px"
                      height="18px"
                    />
                  </Box>
                ) : setMCPIDMutation.isPending ? (
                  <Box display="flex" gap={1} alignItems="center">
                    <LoadingIcon type="circular" size={18} />
                    <Typography
                      variant="caption"
                      color={colors.primary_enabled_text}
                    >
                      Checking...
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <TextButton
                      onClick={() => setMCPIDMutation.mutate()}
                      color="primary"
                      size="xs"
                      disabled={
                        isUpcomingIntegration || !value || !isIntegrated
                      }
                    >
                      Check ID
                    </TextButton>
                  </Box>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              height: "44px",
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "12px",
            },
          }}
        />
        {!Boolean(value) && (
          <Box display="flex" gap="2px" alignItems="center">
            <SvgToIcon
              IconLogo={severities.SeverityInfoTooltipIcon}
              width="13px"
              height="13px"
            />
            <Typography variant="caption" color={colors.text_disabled}>
              Paste the device MCP ID as described in the setup guide
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
